import { Story } from '@/types/cmsTypes';
import { Card } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/text';
import { StatusBadge } from '@/components/cms/ui/status-badge';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LureoButton from '@/common/LureoButton';
import { isMobileDevice } from '@/utils/deviceDetection';
import { DesktopOnlyHint } from '@/components/handoff/DesktopOnlyHint';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { formatDuration } from '@/modules/storyData/utils';
import type { PlanState } from '@/modules/subscriptionPlan/planState';
import { requireReadyPlan } from '@/modules/subscriptionPlan/usePlanState';
import { ArrowRightIcon, ClockIcon, MapPinLineIcon, PencilSimpleLineIcon, LockIcon, UploadSimpleIcon, TrashIcon, MapPinIcon, CubeFocusIcon, AnchorIcon } from '@phosphor-icons/react';
import { StoryLockedDialog } from '@/components/dialogs/story-locked-dialog';
import { LiveStoryActionsDialog } from '@/components/dialogs/live-story-actions-dialog';
import { PublishingWarningDialog } from '@/components/dialogs/publishing-warning-dialog';
import { useStoryLiveActions } from './useStoryLiveActions';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { getSquiggleCropStyle } from '@/utils/squiggleCrop';
import { SquiggleCorner } from '@/components/ui/squiggle-corner';
import { splitDescription } from '@/modules/library/utils/storyAttributionUtils';
import { formatAddressWithoutCountry } from '@/utils/locationUtils';
import { DragHandle } from '@/components/ui/drag-handle';
import { ImageButton } from '@/components/ui/image-button';
import { IconButton } from '@/components/ui/icon-button';
import { shownUrl } from '@/utils/localCopies';

// Anchor category -> icon, mirroring the asset-library mapping in AssetThumbnail.tsx
const ANCHOR_ICONS: Record<string, { Icon: typeof MapPinIcon; label: string }> = {
  geospatial_anchor: { Icon: MapPinIcon, label: 'Geospatial anchor' },
  immersal: { Icon: CubeFocusIcon, label: 'Immersal scan' },
  image_anchor: { Icon: AnchorIcon, label: 'Image anchor' }
};

const mightHaveDrafts = (status?: StoryCardProps['preCalculatedStatus']) =>
  !status || status === 'draft' || status === 'live' || status === 'pending' || status === 'template';

interface StoryCardProps {
  story: Story;
  onEdit: (story: Story) => void;
  onDelete: (story: Story) => void;
  isDraggable?: boolean;
  preCalculatedStatus?: 'live' | 'draft' | 'archived' | 'pending' | 'locked' | 'template';
  preCalculatedDuration?: number | null; // Duration in minutes
  preCalculatedDraftCount?: number; // Batched from parent to avoid the per-card scene_instances N+1
  preCalculatedDraftSceneInstanceId?: string | null; // Latest draft/template instance id for "Open Story"
  preCalculatedAnchorTypes?: string[]; // Anchor categories present in the scene
  preCalculatedUpdatedAt?: string | null; // Latest scene_instance.updated_at, reflects content edits
  plan: PlanState;
  onRefresh?: () => void | Promise<void>;
  statusUnknown?: boolean;
  runWrite?: <T>(write: () => Promise<T>) => Promise<T>;
}

export function StoryCard({
  story,
  onDelete,
  isDraggable = false,
  preCalculatedStatus,
  preCalculatedDuration,
  preCalculatedDraftCount,
  preCalculatedDraftSceneInstanceId,
  preCalculatedAnchorTypes,
  preCalculatedUpdatedAt,
  plan,
  onRefresh,
  statusUnknown = false,
  runWrite
}: StoryCardProps) {
  const navigate = useNavigate();
  const services = useServices();
  
  const [imageFailed, setImageFailed] = useState(false);
  const coverUrl = story.imageCroppedUrl || story.image;
  const hasCoverImage = !!coverUrl && coverUrl !== projectPlaceholder && !imageFailed;

  const [actualStatus, setActualStatus] = useState<'draft' | 'live' | 'archived' | 'pending' | 'locked' | 'template'>(preCalculatedStatus || story.status || 'draft');
  const [duration, setDuration] = useState<number | null>(preCalculatedDuration ?? null);
  const [draftCount, setDraftCount] = useState<number>(preCalculatedDraftCount ?? 0);
  const [draftSceneInstanceId, setDraftSceneInstanceId] = useState<string | null>(
    preCalculatedDraftCount !== undefined ? (preCalculatedDraftSceneInstanceId ?? null) : null
  );
  const [showLockedDialog, setShowLockedDialog] = useState(false);
  const [showLiveActionsDialog, setShowLiveActionsDialog] = useState(false);
  const liveActions = useStoryLiveActions(story, plan, onRefresh, runWrite);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: story.id,
    disabled: !isDraggable
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  // Check story status and draft count. Pre-calculated values apply during render; fetching stays in the effect below.
  const [prevDraftDeps, setPrevDraftDeps] = useState({ storyId: story.id, preCalculatedStatus, preCalculatedDraftCount, preCalculatedDraftSceneInstanceId });
  if (
    prevDraftDeps.storyId !== story.id ||
    prevDraftDeps.preCalculatedStatus !== preCalculatedStatus ||
    prevDraftDeps.preCalculatedDraftCount !== preCalculatedDraftCount ||
    prevDraftDeps.preCalculatedDraftSceneInstanceId !== preCalculatedDraftSceneInstanceId
  ) {
    setPrevDraftDeps({ storyId: story.id, preCalculatedStatus, preCalculatedDraftCount, preCalculatedDraftSceneInstanceId });
    // If preCalculatedStatus is provided, use it
    if (preCalculatedStatus) {
      setActualStatus(preCalculatedStatus);
    }
    // When the parent batched the draft info (ProjectDetail), use it and skip the per-card
    // scene_instances query. This is the fix for the N+1 on the /projects/:id page.
    if (preCalculatedDraftCount !== undefined) {
      setDraftCount(preCalculatedDraftCount);
      setDraftSceneInstanceId(preCalculatedDraftSceneInstanceId ?? null);
    } else if (!mightHaveDrafts(preCalculatedStatus)) {
      setDraftCount(0);
      setDraftSceneInstanceId(null);
    }
  }

  useEffect(() => {
    // We still need to fetch draft count and draft scene instance ID for the "Open Story" button
    // But only if the status indicates we might have drafts
    if (!statusUnknown && preCalculatedDraftCount === undefined && mightHaveDrafts(preCalculatedStatus)) {
      const fetchDraftInfo = async () => {
        try {
          const { data: sceneInstances, error } = await listAll(page => v1.GET('/api/v1/scenes', {
            params: { query: { storyUniqueId: [story.id], status: ['draft', 'template'], sort: '-versionNumber', ...page } },
          }));

          if (error) {
            console.error('Error fetching draft info:', error);
          return;
        }

          setDraftCount(sceneInstances?.length || 0);
          setDraftSceneInstanceId(sceneInstances?.[0]?.uniqueId || null);
        } catch (error) {
          console.error('Error fetching draft info:', error);
        }
      };
      
      fetchDraftInfo();
    }
  }, [story.id, preCalculatedStatus, preCalculatedDraftCount, preCalculatedDraftSceneInstanceId, statusUnknown]);

  // Calculate story duration (or use pre-calculated value, applied during render)
  const [prevDurationDeps, setPrevDurationDeps] = useState({ storyId: story.id, actualStatus, storyDuration: services.storyDuration, preCalculatedDuration });
  if (
    prevDurationDeps.storyId !== story.id ||
    prevDurationDeps.actualStatus !== actualStatus ||
    prevDurationDeps.storyDuration !== services.storyDuration ||
    prevDurationDeps.preCalculatedDuration !== preCalculatedDuration
  ) {
    setPrevDurationDeps({ storyId: story.id, actualStatus, storyDuration: services.storyDuration, preCalculatedDuration });
    if (preCalculatedDuration !== undefined) {
      setDuration(preCalculatedDuration);
    }
  }

  useEffect(() => {
    // If duration is pre-calculated, skip the database call
    if (preCalculatedDuration !== undefined || statusUnknown) {
      return;
    }

    // Otherwise, calculate it from the database
    const calculateDuration = async () => {
      try {
        // Map locked and template status to draft for duration calculation
        const statusForDuration: 'pending' | 'live' | 'draft' | 'archived' = 
          (actualStatus === 'locked' || actualStatus === 'template') ? 'draft' : actualStatus as 'pending' | 'live' | 'draft' | 'archived';
        const storyDuration = await services.storyDuration.calculateStoryDuration(story.id, statusForDuration);
        setDuration(storyDuration);
      } catch (error) {
        console.error('Error calculating story duration:', error);
        setDuration(null);
      }
    };

    if (actualStatus) {
      calculateDuration();
    }
  }, [story.id, actualStatus, services.storyDuration, preCalculatedDuration, statusUnknown]);

  const handleEdit = () => {
    // Navigate to story edit page instead of calling onEdit
    window.location.href = `/story/edit/${story.id}`;
  };

  const handleImageClick = () => {
    // Navigate to story edit page when clicking on the image
    navigate(`/story/edit/${story.id}`);
  };

  const handleOpenInEditor = () => {
    if (draftSceneInstanceId) {
      // Navigate to the editor with the draft scene instance ID
      navigate(`/editor/${draftSceneInstanceId}`);
    } else if (draftCount > 0) {
      // If we have drafts but no specific ID, get the latest version
      const getLatestDraft = async () => {
        try {
          const { data: drafts } = await toResult(v1.GET('/api/v1/scenes', {
            params: { query: { storyUniqueId: [story.id], sort: '-versionNumber', limit: 1 } },
          }));
          
          if (drafts && drafts.items.length > 0) {
            navigate(`/editor/${drafts.items[0].uniqueId}`);
          } else {
            console.error('No draft scene instance ID available');
          }
        } catch (error) {
          console.error('Error getting draft scene instance:', error);
        }
      };
      getLatestDraft();
    } else {
      console.error('No draft scene instance ID available');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(story);
  };

  const anchorIcons = preCalculatedAnchorTypes && preCalculatedAnchorTypes.length > 0 ? (
    <div className="flex items-center gap-1.5 text-[#ffffff]" style={{ mixBlendMode: 'difference' }}>{/* theme-exempt: the difference blend needs pure white to invert the image under it */}
      {preCalculatedAnchorTypes.map((anchor) => {
        const entry = ANCHOR_ICONS[anchor];
        if (!entry) return null;
        const { Icon, label } = entry;
        return <Icon key={anchor} className="h-5 w-5" aria-label={label} />;
      })}
    </div>
  ) : null;

  const updatedLabel = (preCalculatedUpdatedAt ? new Date(preCalculatedUpdatedAt) : story.updatedAt).toLocaleDateString('en-GB');

  const deleteButton = onDelete ? (
    <IconButton label="Delete story" tone="danger-outline" onClick={handleDeleteClick} className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0">
      <TrashIcon className="h-4 w-4" />
    </IconButton>
  ) : null;

  const durationPill = duration !== null ? (
    <span className="flex items-center gap-1.5 rounded-lureo-radius-pill text-lureo-12 tabular-nums text-lureo-ink-subtle leading-4">
      <ClockIcon className="h-4 w-4" />
      {formatDuration(duration)}
    </span>
  ) : null;

  return (
    <div ref={setNodeRef} style={style} className={`relative ${isDraggable ? 'ml-6' : ''}`}>
      {isDraggable && (
        <DragHandle label="Reorder story" {...attributes} {...listeners} className="absolute -left-6 top-1/2 z-10 -translate-y-1/2" />
      )}

      <Card className={`relative ${isDragging ? 'z-lureo-layer-overlay' : ''}`}>
        <div className="flex flex-col h-full sm:flex-row">
          {/* Image Section — full-width on top (mobile), side strip on sm+ */}
          <div className="relative shrink-0">
            {hasCoverImage ? (
              <div className="relative h-52 w-full overflow-hidden corner-t-lureo-radius-lg sm:h-full sm:w-44 sm:corner-l-lureo-radius-lg sm:rounded-tr-none lg:w-40 xl:w-48">
                <img
                  src={shownUrl(coverUrl)}
                  alt={story.title}
                  loading="lazy"
                  decoding="async"
                  onError={() => setImageFailed(true)}
                  className="w-full h-full object-cover"
                />
                <SquiggleCorner id={story.id} shape="tall" />
              </div>
            ) : (
              <ImageButton
                label="Add story image"
                hint={<UploadSimpleIcon />}
                onClick={handleImageClick}
                className="h-52 w-full rounded-b-none border-0 sm:h-full sm:w-44 sm:corner-l-lureo-radius-lg sm:rounded-r-none lg:w-40 xl:w-48"
              >
                <span className="absolute inset-0" style={getSquiggleCropStyle(story.id, { scaleRange: [3, 4], size: 'quarter' })} />
              </ImageButton>
            )}
            {/* Status Badge - Top Right of Image */}
            <div className="absolute -left-2 -top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-lureo-aqua font-bold text-lureo-ink-strong">
              {story.order}
            </div>

            <div className="absolute right-2 top-2 z-20 flex items-center gap-2">
              {!statusUnknown && <StatusBadge status={actualStatus} className="shrink-0" />}
              <span className="sm:hidden">{deleteButton}</span>
            </div>

            {/* Anchor-type indicator — shows how the scene is anchored. mix-blend-mode
                difference makes a white icon render as the inverse of whatever
                is behind it, so it always reads regardless of image content. */}
            <div className="absolute bottom-2 left-2 z-20 hidden sm:block">{anchorIcons}</div>

            <div className="absolute inset-x-0 bottom-0 z-20 bg-linear-to-t from-lureo-photo-shade-tint/85 via-lureo-photo-shade-tint/45 to-transparent px-3 pb-2.5 pt-8 sm:hidden">
              <h3 className="truncate text-lureo-16 font-semibold leading-tight text-lureo-on-emphasis">{story.title}</h3>
              <div className="mt-1 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 text-lureo-on-emphasis">
                  {durationPill && (
                    <span className="flex items-center gap-1.5 text-lureo-12 tabular-nums text-lureo-on-emphasis leading-4">
                      <ClockIcon className="h-4 w-4" />
                      {formatDuration(duration as number)}
                    </span>
                  )}
                  {anchorIcons}
                </div>
                <span className="shrink-0 text-lureo-12 text-lureo-on-emphasis leading-4">{updatedLabel}</span>
              </div>
            </div>
          </div>

          {/* Content Section — below image on mobile, right side on sm+ */}
          <div className="flex flex-1 flex-col justify-between min-w-0 p-3 sm:p-0 sm:ps-2 sm:pt-1 lg:ps-4">
            {/* Header Row — folded onto the image on mobile */}
            <div className="mb-2 hidden items-start justify-between gap-2 sm:flex">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <CardTitle as="h3" className="truncate text-lureo-16 text-lureo-ink-subtle lg:text-lureo-18 lg:leading-7">{story.title}</CardTitle>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-lureo-12 text-lureo-ink-subtle max-w-[80px] lg:max-w-[120px] xl:max-w-none leading-4">
                  <span className="hidden lg:inline">Updated </span>
                  {updatedLabel}
                </span>
                {deleteButton}
              </div>
            </div>

            {/* Description */}
            <p className="mb-5 line-clamp-2 text-lureo-16 text-lureo-ink-subtle sm:mb-3 sm:text-lureo-14 leading-6 sm:leading-5">
              {story.description ? splitDescription(story.description).userText : story.description}
            </p>

            {/* Location Info */}
            {story.locationInfo?.address && story.locationInfo.address !== 'null' && (
              <div className="mb-1 flex items-center gap-2 text-lureo-12 text-lureo-ink-subtle min-w-0 leading-4">
                <span className="flex items-center gap-1.5 rounded-lureo-radius-pill bg-lureo-neutral/20 text-lureo-12 text-lureo-ink-subtle leading-4">
                  <MapPinLineIcon className=" h-4 w-4" />
                  {formatAddressWithoutCountry(story.locationInfo.address)}
                </span>
              </div>
            )}

            {/* Media Type and Duration — duration moves onto the image on mobile */}
            <div className="mb-1 hidden h-6 items-center gap-2 sm:flex md:gap-3">
              {story.media && duration === null && (
                <span className="rounded-lureo-radius-pill bg-lureo-accent/10 py-1 text-lureo-12 text-lureo-teal leading-4">{story.media.type}</span>
              )}
              {durationPill}
            </div>

            {/* Action Buttons - Bottom Row (wrap on narrow cards) */}
            <div className="flex flex-wrap items-center gap-2 sm:justify-between">
              <LureoButton
                variant="outline-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit();
                }}
                className="h-9 lg:h-11 rounded-lureo-radius-button-pill px-2 lg:px-3 flex-1 sm:flex-none min-h-[44px] sm:min-h-0">
                <PencilSimpleLineIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                Edit Details
              </LureoButton>
              {statusUnknown ? null : actualStatus === 'locked' ? (
                <LureoButton
                  variant="outline-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (requireReadyPlan(plan)) setShowLockedDialog(true);
                  }}
                  className="h-9 lg:h-11 rounded-lureo-radius-button-pill px-2 lg:px-4 flex-1 sm:flex-none min-h-[44px] sm:min-h-0">
                  <span className="hidden lg:inline">Locked</span>
                  <LockIcon className="h-4 w-4" />
                </LureoButton>
              ) : actualStatus === 'live' || actualStatus === 'pending' ? (
                <LureoButton
                  variant="outline-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLiveActionsDialog(true);
                  }}
                  className="h-9 lg:h-11 rounded-lureo-radius-button-pill px-2 lg:px-5 w-full sm:w-auto sm:flex-none min-h-[44px] sm:min-h-0">
                  Open Story
                  <ArrowRightIcon className="h-4 w-4 lg:h-5 lg:w-5" />
                </LureoButton>
              ) : draftCount > 0 && (
                <>
                <LureoButton
                  variant="outline-3"
                  disabled={isMobileDevice()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenInEditor();
                  }}
                  className="h-9 lg:h-11 rounded-lureo-radius-button-pill px-2 lg:px-5 w-full sm:w-auto sm:flex-none min-h-[44px] sm:min-h-0">
                  Open Story
                  <ArrowRightIcon className="h-4 w-4 lg:h-5 lg:w-5" />
                </LureoButton>
                {isMobileDevice() && <DesktopOnlyHint className="block w-full text-center" />}
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Story Locked Dialog */}
      {plan.plan && (
        <StoryLockedDialog
          open={showLockedDialog}
          onOpenChange={setShowLockedDialog}
          storyName={story.title}
          currentPlan={plan.plan}
          maxStories={services.subscriptionPlan.getFeatureLimits(plan.plan).maxStories}
        />
      )}

      {/* Live Story Actions Dialog */}
      <LiveStoryActionsDialog
        open={showLiveActionsDialog}
        onOpenChange={setShowLiveActionsDialog}
        storyName={story.title}
        status={actualStatus === 'pending' ? 'pending' : 'live'}
        onAction={(action) => {
          if (action === 'edit-copy') void liveActions.editCopy();
          else if (action === 'unpublish') void liveActions.unpublish();
          else if (action === 'archive') void liveActions.archive();
        }}
      />

      {liveActions.warning && (
        <PublishingWarningDialog
          open
          onOpenChange={(open) => { if (!open) liveActions.clearWarning(); }}
          onConfirm={liveActions.warning.onConfirm}
          onCancel={liveActions.clearWarning}
          warningType={liveActions.warning.type}
          storyName={story.title}
          liveLinkUrl={liveActions.warning.liveLinkUrl}
        />
      )}
    </div>
  );
}
